import React, { useEffect, useState } from "react";
import {
    Typography,
    Paper,
    Stack,
    Chip,
    Box,
    Divider,
    TextField,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import BreadcrumbsNav from "../components/BreadcrumbsNav";
import NewsletterSignup from "../components/NewsletterSignup";

interface BlogPostMeta {
    slug: string;
    title: string;
    date: string;
    summary: string;
    file: string;
    tags: string[];
    readingTime: string;
}

const Blog: React.FC = () => {
    const [posts, setPosts] = useState<BlogPostMeta[]>([]);
    const [activeTag, setActiveTag] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        fetch(`${process.env.PUBLIC_URL}/posts/blog-index.json`)
            .then((res) => res.json())
            .then((data: BlogPostMeta[]) => {
                const sorted = data.sort((a, b) => (a.date < b.date ? 1 : -1));
                setPosts(sorted);
            });
    }, []);

    const allTags = Array.from(new Set(posts.flatMap((p) => p.tags || []))).sort();

    const filteredPosts = posts
        .filter((post) =>
            activeTag ? post.tags.includes(activeTag) : true
        )
        .filter((post) =>
            searchQuery
                ? post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                post.summary.toLowerCase().includes(searchQuery.toLowerCase())
                : true
        );

    const grouped = filteredPosts.reduce(
        (acc: Record<string, BlogPostMeta[]>, post) => {
            const year = new Date(post.date).getFullYear().toString();
            acc[year] = acc[year] || [];
            acc[year].push(post);
            return acc;
        },
        {}
    );

    return (
        <Box>
            <BreadcrumbsNav />

            <NewsletterSignup />

            <TextField
                label="Search blog posts"
                variant="outlined"
                fullWidth
                sx={{ my: 2 }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
            />

            <Box mb={3}>
                <Typography variant="subtitle1" color="text.secondary">
                    Filter by tag:
                </Typography>
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                    {allTags.map((tag) => (
                        <Chip
                            key={tag}
                            label={tag}
                            color={tag === activeTag ? "primary" : "default"}
                            onClick={() => setActiveTag(tag === activeTag ? null : tag)}
                            clickable
                            sx={{ mb: 1 }}
                        />
                    ))}
                </Stack>
            </Box>

            {Object.entries(grouped).map(([year, posts]) => (
                <Box key={year} mb={4}>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                        {year}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={3}>
                        <AnimatePresence>
                            {posts.map((post) => (
                                <motion.div
                                    key={post.slug}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Paper sx={{ p: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            <RouterLink
                                                to={`/blog/${post.slug}`}
                                                style={{ textDecoration: "none", color: "inherit" }}
                                            >
                                                {post.title}
                                            </RouterLink>
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {new Date(post.date).toLocaleDateString()} • {post.readingTime}
                                        </Typography>
                                        <Typography variant="body1" sx={{ my: 1 }}>
                                            {post.summary}
                                        </Typography>
                                        <Stack direction="row" spacing={1} mt={1}>
                                            {post.tags.map((tag) => (
                                                <Chip key={tag} size="small" label={tag} variant="outlined" />
                                            ))}
                                        </Stack>
                                    </Paper>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </Stack>
                </Box>
            ))}
        </Box>
    );
};

export default Blog;
